import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { PartnerThemeConfig, PartnerBySubfolderOutput } from 'frontend.client.api';
import { PartnerStateService } from './partner-state.service';

export interface ThemeState {
  currentTheme?: string;
  partnerTheme?: PartnerThemeConfig;
  fallbackActive: boolean;
  isLoading: boolean;
  error?: string;
  customProperties?: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  public themeState$ = new BehaviorSubject<ThemeState>({
    fallbackActive: false,
    isLoading: false
  });

  private partnerConfigCache = new Map<string, PartnerBySubfolderOutput>();
  private cacheExpiry = new Map<string, number>();
  private cacheVersions = new Map<string, string>();
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  private readonly BACKGROUND_REFRESH_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
  private backgroundRefreshTimer?: number;

  constructor(
    private http: HttpClient,
    private partnerStateService: PartnerStateService
  ) {
    // Automatically load themes when partner changes
    this.initializePartnerThemeIntegration();
  }

  /**
   * Initialize integration with partner state changes
   */
  private initializePartnerThemeIntegration(): void {
    this.partnerStateService.partnerState.subscribe(partnerState => {
      if (partnerState.partnerData) {
        this.loadPartnerTheme(partnerState.partnerData);
      } else if (partnerState.subFolder === undefined) {
        // No partner context, clear theme
        this.removeCurrentTheme();
        this.updateState({
          currentTheme: undefined,
          partnerTheme: undefined,
          fallbackActive: false,
          customProperties: undefined
        });
      }
    });
  }

  /**
   * Get current partner context
   */
  getCurrentPartner(): PartnerBySubfolderOutput | undefined {
    return this.partnerStateService.partner;
  }

  /**
   * Get current partner subfolder
   */
  getCurrentPartnerSubfolder(): string | undefined {
    return this.partnerStateService.partnerState.getValue().subFolder;
  }

  /**
   * Get current CSS custom properties
   */
  getCustomProperties(): Record<string, string> {
    return this.themeState$.getValue().customProperties || {};
  }

  /**
   * Load partner theme based on partner configuration from DynamoDB
   */
  async loadPartnerTheme(partnerData?: PartnerBySubfolderOutput): Promise<void> {
    this.updateState({ isLoading: true, error: undefined });

    try {
      if (!partnerData?.theme) {
        // No partner theme configured, use legacy theme loading
        await this.loadLegacyTheme(partnerData?.themeTemplateToken);
        const customProps = await this.extractCustomProperties();
        this.updateState({
          isLoading: false,
          currentTheme: partnerData?.themeTemplateToken,
          partnerTheme: undefined,
          fallbackActive: false,
          customProperties: customProps
        });
        return;
      }

      const success = await this.loadPartnerCssFile(partnerData.theme);

      if (success) {
        const customProps = await this.extractCustomProperties();
        this.updateState({
          isLoading: false,
          currentTheme: partnerData.theme.cssFileName,
          partnerTheme: partnerData.theme,
          fallbackActive: false,
          customProperties: customProps
        });
      } else {
        // Partner theme failed, try fallback
        await this.handleThemeFallback(partnerData);
      }
    } catch (error) {
      console.error('Theme loading failed:', error);
      await this.handleThemeFallback(partnerData);
    }
  }

  /**
   * Legacy theme switching for backward compatibility
   */
  async switchTheme(theme?: string): Promise<void> {
    if (theme && this.themeState$.getValue().currentTheme !== theme) {
      this.removeCurrentTheme();
      await this.loadLegacyTheme(theme);
      const customProps = await this.extractCustomProperties();
      this.updateState({
        currentTheme: theme,
        partnerTheme: undefined,
        fallbackActive: false,
        customProperties: customProps
      });
    } else if (!theme) {
      this.removeCurrentTheme();
      this.updateState({
        currentTheme: undefined,
        partnerTheme: undefined,
        fallbackActive: false,
        customProperties: undefined
      });
    }
  }

  /**
   * Apply CSS custom properties to root element
   */
  applyCustomProperties(properties: Record<string, string>): void {
    const root = document.documentElement;
    Object.entries(properties).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    this.updateState({
      customProperties: { ...this.getCustomProperties(), ...properties }
    });
  }

  /**
   * Remove CSS custom properties from root element
   */
  removeCustomProperties(propertyNames: string[]): void {
    const root = document.documentElement;
    propertyNames.forEach(property => {
      root.style.removeProperty(property);
    });

    const currentProps = this.getCustomProperties();
    propertyNames.forEach(prop => delete currentProps[prop]);
    this.updateState({ customProperties: currentProps });
  }

  /**
   * Get partner configuration with caching and version-based invalidation
   */
  getPartnerConfiguration(partnerSubfolder: string, forceRefresh = false): Observable<PartnerBySubfolderOutput> {
    // Check cache first (unless forced refresh)
    if (!forceRefresh) {
      const cached = this.partnerConfigCache.get(partnerSubfolder);
      const cacheTime = this.cacheExpiry.get(partnerSubfolder);

      if (cached && cacheTime && Date.now() < cacheTime) {
        // Start background refresh for version checking
        this.startBackgroundRefresh();
        return of(cached);
      }
    }

    // Fetch from API
    return this.http.get<PartnerBySubfolderOutput>(`/api/v1/partners/subfolder/${partnerSubfolder}`)
      .pipe(
        tap(config => {
          this.updatePartnerCache(partnerSubfolder, config);
          // Start background refresh for future version checks
          this.startBackgroundRefresh();
        }),
        catchError(error => {
          console.error('Failed to fetch partner configuration:', error);
          // Return cached version if available during error
          const cached = this.partnerConfigCache.get(partnerSubfolder);
          if (cached) {
            console.warn('Using cached partner configuration due to API error');
            return of(cached);
          }
          throw error;
        })
      );
  }

  /**
   * Clear partner configuration cache
   */
  clearCache(): void {
    this.partnerConfigCache.clear();
    this.cacheExpiry.clear();
    this.cacheVersions.clear();
    this.stopBackgroundRefresh();
  }

  /**
   * Invalidate cache for specific partner
   */
  invalidatePartnerCache(subfolder: string): void {
    this.partnerConfigCache.delete(subfolder);
    this.cacheExpiry.delete(subfolder);
    this.cacheVersions.delete(subfolder);
  }

  /**
   * Check if cached partner configuration needs refresh based on version
   */
  private needsVersionRefresh(subfolder: string, currentVersion?: string): boolean {
    if (!currentVersion) return false;

    const cachedVersion = this.cacheVersions.get(subfolder);
    return cachedVersion !== currentVersion;
  }

  /**
   * Update cache with new partner configuration and version
   */
  private updatePartnerCache(subfolder: string, config: PartnerBySubfolderOutput): void {
    this.partnerConfigCache.set(subfolder, config);
    this.cacheExpiry.set(subfolder, Date.now() + this.CACHE_DURATION_MS);

    // Track version for cache invalidation
    if (config.theme?.version) {
      this.cacheVersions.set(subfolder, config.theme.version);
    }
  }

  /**
   * Start background refresh for active partner configurations
   */
  private startBackgroundRefresh(): void {
    if (this.backgroundRefreshTimer) return;

    this.backgroundRefreshTimer = window.setInterval(() => {
      this.refreshActivePartnerConfigurations();
    }, this.BACKGROUND_REFRESH_INTERVAL_MS);
  }

  /**
   * Stop background refresh
   */
  private stopBackgroundRefresh(): void {
    if (this.backgroundRefreshTimer) {
      window.clearInterval(this.backgroundRefreshTimer);
      this.backgroundRefreshTimer = undefined;
    }
  }

  /**
   * Refresh partner configurations in background to detect updates
   */
  private async refreshActivePartnerConfigurations(): Promise<void> {
    const currentPartner = this.getCurrentPartner();
    if (!currentPartner) return;

    const subfolder = this.getCurrentPartnerSubfolder();
    if (!subfolder) return;

    try {
      // Fetch latest partner configuration without using cache
      const response = await this.http.get<PartnerBySubfolderOutput>(
        `/api/partner/subfolder/${subfolder}`
      ).toPromise();

      if (response) {
        const cachedConfig = this.partnerConfigCache.get(subfolder);

        // Check if configuration has been updated
        if (cachedConfig && this.hasConfigurationChanged(cachedConfig, response)) {
          console.log(`Partner configuration updated for ${subfolder}, refreshing theme...`);

          // Update cache with new configuration
          this.updatePartnerCache(subfolder, response);

          // Reload theme if it changed
          if (this.hasThemeChanged(cachedConfig.theme, response.theme)) {
            await this.loadPartnerTheme(response);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to refresh partner configuration in background:', error);
    }
  }

  /**
   * Check if partner configuration has changed
   */
  private hasConfigurationChanged(cached: PartnerBySubfolderOutput, current: PartnerBySubfolderOutput): boolean {
    // Compare theme versions if available
    if (cached.theme?.version && current.theme?.version) {
      return cached.theme.version !== current.theme.version;
    }

    // Fallback: compare theme configuration objects
    return JSON.stringify(cached.theme) !== JSON.stringify(current.theme);
  }

  /**
   * Check if theme configuration has changed
   */
  private hasThemeChanged(cachedTheme?: PartnerThemeConfig, currentTheme?: PartnerThemeConfig): boolean {
    if (!cachedTheme && !currentTheme) return false;
    if (!cachedTheme || !currentTheme) return true;

    return (
      cachedTheme.cssFileName !== currentTheme.cssFileName ||
      cachedTheme.version !== currentTheme.version ||
      cachedTheme.fallbackEnabled !== currentTheme.fallbackEnabled
    );
  }

  /**
   * Force reload theme for current partner
   */
  async reloadTheme(): Promise<void> {
    const currentPartner = this.getCurrentPartner();
    if (currentPartner) {
      await this.loadPartnerTheme(currentPartner);
    }
  }

  /**
   * Load partner CSS file from assets
   */
  private async loadPartnerCssFile(themeConfig: PartnerThemeConfig): Promise<boolean> {
    try {
      const cssUrl = `assets/themes/partners/${themeConfig.cssFileName}`;

      // Validate CSS file exists and is valid
      const response = await fetch(cssUrl);
      if (!response.ok) {
        console.warn(`Partner theme file not found: ${cssUrl}`);
        return false;
      }

      const cssContent = await response.text();
      if (!this.validateCssContent(cssContent)) {
        console.warn(`Partner theme file is malformed: ${cssUrl}`);
        return false;
      }

      // Remove existing theme and apply new one
      this.removeCurrentTheme();

      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = cssUrl;
      linkElement.id = 'partner-theme';

      document.head.appendChild(linkElement);

      // Wait for stylesheet to load before extracting properties
      await new Promise((resolve) => {
        linkElement.onload = resolve;
        setTimeout(resolve, 100); // Fallback timeout
      });

      return true;
    } catch (error) {
      console.error('Failed to load partner CSS file:', error);
      return false;
    }
  }

  /**
   * Legacy theme loading for backward compatibility
   */
  private async loadLegacyTheme(theme?: string): Promise<boolean> {
    if (!theme) return false;

    try {
      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = `assets/themes/${theme}/_variables.css`;
      linkElement.id = 'legacy-theme';

      const preload = (await fetch(linkElement.href)).ok;
      if (preload) {
        document.head.appendChild(linkElement);

        // Wait for stylesheet to load
        await new Promise((resolve) => {
          linkElement.onload = resolve;
          setTimeout(resolve, 100); // Fallback timeout
        });
      }
      return preload;
    } catch (error) {
      console.error('Failed to load legacy theme:', error);
      return false;
    }
  }

  /**
   * Extract CSS custom properties from computed styles
   */
  private async extractCustomProperties(): Promise<Record<string, string>> {
    try {
      // Allow time for styles to apply
      await new Promise(resolve => setTimeout(resolve, 50));

      const root = document.documentElement;
      const computedStyle = getComputedStyle(root);
      const customProperties: Record<string, string> = {};

      // Get all CSS custom properties (variables starting with --)
      const allStyles = Array.from(computedStyle);
      allStyles.forEach(property => {
        if (property.startsWith('--')) {
          const value = computedStyle.getPropertyValue(property);
          if (value && value.trim()) {
            customProperties[property] = value.trim();
          }
        }
      });

      // Ensure Stencil components can access theme properties
      this.propagatePropertiesToStencilComponents(customProperties);

      return customProperties;
    } catch (error) {
      console.error('Failed to extract custom properties:', error);
      return {};
    }
  }

  /**
   * Propagate CSS custom properties to Stencil components
   */
  private propagatePropertiesToStencilComponents(properties: Record<string, string>): void {
    // Find all Stencil components (custom elements)
    const stencilComponents = document.querySelectorAll('*:not(:defined)');

    stencilComponents.forEach(element => {
      // Apply custom properties to component's style
      const htmlElement = element as HTMLElement;
      Object.entries(properties).forEach(([property, value]) => {
        htmlElement.style.setProperty(property, value);
      });
    });

    // Also ensure all existing custom elements get updated
    const customElements = document.querySelectorAll(':defined');
    customElements.forEach(element => {
      if (element.tagName.includes('-')) { // Likely a custom element
        const htmlElement = element as HTMLElement;
        Object.entries(properties).forEach(([property, value]) => {
          htmlElement.style.setProperty(property, value);
        });
      }
    });
  }

  /**
   * Handle theme fallback scenarios
   */
  private async handleThemeFallback(partnerData?: PartnerBySubfolderOutput): Promise<void> {
    const fallbackEnabled = partnerData?.theme?.fallbackEnabled ?? true;

    if (fallbackEnabled) {
      // Try legacy theme first
      if (partnerData?.themeTemplateToken) {
        const legacySuccess = await this.loadLegacyTheme(partnerData.themeTemplateToken);
        if (legacySuccess) {
          const customProps = await this.extractCustomProperties();
          this.updateState({
            isLoading: false,
            currentTheme: partnerData.themeTemplateToken,
            partnerTheme: partnerData.theme,
            fallbackActive: true,
            error: 'Partner theme unavailable, using fallback',
            customProperties: customProps
          });
          return;
        }
      }

      // Final fallback - no theme (use default styles)
      this.removeCurrentTheme();
      this.updateState({
        isLoading: false,
        currentTheme: undefined,
        partnerTheme: partnerData?.theme,
        fallbackActive: true,
        error: 'All themes unavailable, using default styles',
        customProperties: undefined
      });
    } else {
      // Fallback disabled - fail completely
      this.removeCurrentTheme();
      this.updateState({
        isLoading: false,
        currentTheme: undefined,
        partnerTheme: partnerData?.theme,
        fallbackActive: false,
        error: 'Partner theme unavailable and fallback disabled',
        customProperties: undefined
      });
    }

    // Log fallback for debugging
    console.warn('Theme fallback activated for partner:', partnerData?.partnerId);

    // Development environment notification
    if (!this.isProduction()) {
      this.showDevelopmentNotification('Theme fallback activated');
    }
  }

  /**
   * Basic CSS content validation
   */
  private validateCssContent(cssContent: string): boolean {
    // Basic validation - check if content looks like CSS
    const cssPattern = /[\w-]+\s*:\s*[^;]+;/;
    const rootVariablePattern = /:root\s*{[^}]*}/;

    return cssPattern.test(cssContent) || rootVariablePattern.test(cssContent);
  }

  /**
   * Remove current theme elements
   */
  private removeCurrentTheme(): void {
    const themeSelectors = ['#partner-theme', '#legacy-theme', '#customer-theme'];
    themeSelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        element.remove();
      }
    });
  }

  /**
   * Update theme state
   */
  private updateState(updates: Partial<ThemeState>): void {
    const currentState = this.themeState$.getValue();
    this.themeState$.next({ ...currentState, ...updates });
  }

  /**
   * Check if running in production
   */
  private isProduction(): boolean {
    return (window as any)['environment']?.production === true;
  }

  /**
   * Show development notification
   */
  private showDevelopmentNotification(message: string): void {
    if (typeof console !== 'undefined') {
      console.info(`🎨 Theme Service: ${message}`);
    }
  }

  /**
   * Manual cache invalidation for external triggering
   */
  invalidateCache(subfolder?: string): void {
    if (subfolder) {
      this.invalidatePartnerCache(subfolder);
      console.log(`Cache invalidated for partner: ${subfolder}`);
    } else {
      this.clearCache();
      console.log('All partner caches cleared');
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    totalCached: number;
    cacheKeys: string[];
    versions: Record<string, string>;
    backgroundRefreshActive: boolean;
  } {
    return {
      totalCached: this.partnerConfigCache.size,
      cacheKeys: Array.from(this.partnerConfigCache.keys()),
      versions: Object.fromEntries(this.cacheVersions),
      backgroundRefreshActive: !!this.backgroundRefreshTimer
    };
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.stopBackgroundRefresh();
    this.clearCache();
  }
}
